import type {
  Participant,
  ParticipantBehaviour,
  ParticipantEmbeddableForm,
  ParticipantQr,
} from '@piccolohealth/pbs-common';
import { ChakraV3 } from '@piccolohealth/ui';
import { P } from '@piccolohealth/util';
import React from 'react';
import { usePermission } from '../../hooks/usePermission';
import { ManageParticipantQRLabelsControl } from '../labels/ManageParticipantQRLabelsControl';
import { ParticipantQRActionsMenu } from './ParticipantQRActionsMenu';
import { ParticipantQRCode } from './ParticipantQRCode';
import { ParticipantQRCopyImageButton } from './ParticipantQRCopyImageButton';
import { ParticipantQRCopyLinkButton } from './ParticipantQRCopyLinkButton';
import { ParticipantQRRecordingsPopover } from './ParticipantQRRecordingsPopover';
import { ParticipantQRStatusTag } from './ParticipantQRStatusTag';

interface Props {
  qr: ParticipantQr;
  participantBehaviours: ParticipantBehaviour[];
  participant: Participant;
  participantEmbeddableForms: ParticipantEmbeddableForm[];
  isDisabled?: boolean;
}

export const ParticipantQRListItem = (props: Props) => {
  const { qr, participantBehaviours, participant, participantEmbeddableForms } = props;

  const updateParticipantQRPermission = usePermission('update', 'participantQr');
  const isDisabled = props.isDisabled || !updateParticipantQRPermission;

  return (
    <ChakraV3.HStack w='full' align='stretch' gap={4}>
      <ParticipantQRCode qrId={qr.id} h={24} w={24} flexShrink={0} />
      <ChakraV3.Stack minW='180px' py={2} gap={0}>
        <ChakraV3.Text fontWeight='semibold' fontSize='lg'>
          {qr.name}
        </ChakraV3.Text>
        <ChakraV3.Stack fontSize='sm' color='secondary'>
          <ParticipantQRRecordingsPopover qr={qr} />
        </ChakraV3.Stack>
        <ChakraV3.Spacer />
        <ChakraV3.HStack align='stretch'>
          <ParticipantQRCopyLinkButton qrId={qr.id} participant={participant} variant='plain'>
            Copy link
          </ParticipantQRCopyLinkButton>
          <ParticipantQRCopyImageButton qrId={qr.id} participant={participant} variant='plain'>
            Copy QR code
          </ParticipantQRCopyImageButton>
        </ChakraV3.HStack>
      </ChakraV3.Stack>
      <ChakraV3.Stack w='full' py={2}>
        <ChakraV3.SimpleGrid
          w='full'
          columns={2}
          templateColumns='max-content auto'
          alignItems='baseline'
          rowGap={2}
          columnGap={8}
        >
          <ChakraV3.Stack fontSize='sm' color='secondary'>
            Status:
          </ChakraV3.Stack>
          <ChakraV3.Text fontSize='sm' lineClamp={3} pl={3}>
            <ParticipantQRStatusTag status={qr.status} size='sm' />
          </ChakraV3.Text>

          <ChakraV3.Stack fontSize='sm' color='secondary'>
            Behaviours:
          </ChakraV3.Stack>
          <ChakraV3.Text fontSize='sm' lineClamp={3} pl={3}>
            {P.isEmpty(qr.behaviours)
              ? '-'
              : qr.behaviours.map((behaviour) => behaviour.name).join(', ')}
          </ChakraV3.Text>

          <ChakraV3.Stack fontSize='sm' color='secondary'>
            Forms:
          </ChakraV3.Stack>
          <ChakraV3.Text fontSize='sm' lineClamp={3} pl={3}>
            {P.isEmpty(qr.embeddableForms)
              ? '-'
              : qr.embeddableForms.map((form) => form.name).join(', ')}
          </ChakraV3.Text>

          <ChakraV3.Stack fontSize='sm' color='secondary'>
            Labels:
          </ChakraV3.Stack>
          <ManageParticipantQRLabelsControl
            participantId={participant.id}
            qrId={qr.id}
            value={qr.labels.map((l) => l.id)}
            isDisabled={isDisabled}
          />
        </ChakraV3.SimpleGrid>
      </ChakraV3.Stack>
      <ChakraV3.Stack>
        <ParticipantQRActionsMenu
          participantId={participant.id}
          qr={qr}
          participantBehaviours={participantBehaviours}
          participantEmbeddableForms={participantEmbeddableForms}
          isDisabled={isDisabled}
        />
      </ChakraV3.Stack>
    </ChakraV3.HStack>
  );
};
